import React, { useState, useContext, useEffect } from 'react';
import { pagoTransferencia, obtenerFechasPago, verificarPagosAnterioresHistorial } from '../../services/pagosServices';
import { obtenerMatriculaPorId, verificarHijos, actualizarMatricula } from '../../services/matriculaService';
import { obtenerCentro, obtenerCuentasBancarias, obtenerPlanes } from '../../services/centroService';
import { ToastContainer, toast } from 'react-toastify';
import { FaFile, FaTrash, FaExchangeAlt } from 'react-icons/fa';
import { CheckCircle, Loader } from 'lucide-react';
import 'react-toastify/dist/ReactToastify.css';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const Transferencia = ({ planSeleccionado }) => {
    const [comprobante, setComprobante] = useState(null);
    const [amount, setAmount] = useState(0);
    const [matriculaId, setMatriculaId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [imagenAmpliada, setImagenAmpliada] = useState(false);
    const [cuentasBancarias, setCuentasBancarias] = useState([]);
    const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [nombreArchivo, setNombreArchivo] = useState('');
    const [mostrarPago, setMostrarPago] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [archivoError, setArchivoError] = useState('');

    const [planes, setPlanes] = useState([]);
    const [planActual, setPlanActual] = useState(null);
    // planSeleccionado ahora viene como prop
    const [tienePagosAnteriores, setTienePagosAnteriores] = useState(false);
    const [loadingPlanes, setLoadingPlanes] = useState(false);


    const verificarEstadoPago = async (idMatricula) => {
        try {
            const fechasResponse = await obtenerFechasPago(idMatricula);
            
            if (fechasResponse.success) {
                const fechaActual = new Date();
                fechaActual.setHours(0, 0, 0, 0);
                
                if (fechasResponse.data && fechasResponse.data.length > 0) {
                    const ultimoRegistro = fechasResponse.data[fechasResponse.data.length - 1];
                    const proximaFechaPago = ultimoRegistro ? new Date(ultimoRegistro.proxima_fecha_pago) : null;
                    if (proximaFechaPago) {
                        proximaFechaPago.setHours(0, 0, 0, 0);
                    }
                    
                    let requierePago = true;
                    if (ultimoRegistro) {
                        if (ultimoRegistro.estado_verificacion === 'Rechazado') {
                            requierePago = true; // Siempre requiere pago si fue rechazado
                        } else if (ultimoRegistro.estado_verificacion === 'Pendiente') {
                            // Verificar si es una transferencia pendiente de verificación
                            const tieneTransferenciaPendiente = ultimoRegistro.tipo_pago === 'Transferencia' && 
                                                               ultimoRegistro.comprobante_pago;
                            
                            if (tieneTransferenciaPendiente) {
                                requierePago = false; // No requiere pago si hay transferencia en revisión
                            } else {
                                // Estado pendiente sin transferencia - verificar fecha
                                requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : true;
                            }
                        } else if (ultimoRegistro.estado_verificacion === 'Verificado' || ultimoRegistro.estado === 'Completado') {
                            requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : false;
                        } else {
                            requierePago = proximaFechaPago ? fechaActual >= proximaFechaPago : true;
                        }
                    }
                    
                    setMostrarPago(requierePago);
                } else {
                    setMostrarPago(true); // Si no hay control de pagos, permitir pago
                }
            } else {
                console.warn('Error en la respuesta de fechas de pago para transferencia:', fechasResponse);
                setMostrarPago(true); // En caso de error, permitir pago
            }
        } catch (err) {
            console.error('Error al verificar estado de pago en transferencia:', err);
            setMostrarPago(true); // En caso de error, permitir pago
            setError('Error al verificar estado de pago');
        }
    };

    const cargarPlanes = async () => {
        try {
            setLoadingPlanes(true);
            const response = await obtenerPlanes();
            if (response.success && response.data) {
                setPlanes(response.data);
            }
        } catch (error) {
            console.error('Error al cargar planes:', error);
            showErrorToast('Error al cargar los planes disponibles');
        } finally {
            setLoadingPlanes(false);
        }
    };

    // useEffect para actualizar el amount cuando cambie planSeleccionado
    useEffect(() => {
        if (planSeleccionado && planSeleccionado.monto) {
            const precio = Number(planSeleccionado.monto);
            setAmount(isNaN(precio) ? 0 : precio);
        }
    }, [planSeleccionado]);

    const verificarPagosAnteriores = async (idMatricula) => {
        try {
            const response = await verificarPagosAnterioresHistorial(idMatricula);
            if (response.success) {
                return response.tienePagosAnteriores;
            }
            return false;
        } catch (error) {
            console.error('Error al verificar pagos anteriores:', error);
            return false;
        }
    };

    const handleSeleccionarPlan = (plan, e) => {
        // Prevenir comportamiento por defecto para evitar recarga de página
        if (e && e.preventDefault) {
            e.preventDefault();
        }
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        // planSeleccionado ahora viene como prop, no se puede modificar aquí
        setAmount(plan.monto);
    };



    // Función para renderizar la descripción con beneficios como viñetas
    const renderDescripcion = (descripcion) => {
        if (!descripcion) return null;
        
        const partes = descripcion.split(';');
        
        if (partes.length <= 1) {
            return <p className="text-sm text-gray-600">{descripcion}</p>;
        }
        
        const descripcionBase = partes[0].trim();
        const beneficios = partes.slice(1).filter(beneficio => beneficio.trim());
        
        return (
            <div>
                {descripcionBase && (
                    <p className="text-sm text-gray-600 mb-3">{descripcionBase}</p>
                )}
                {beneficios.length > 0 && (
                    <div>
                        <p className="font-semibold text-gray-700 mb-2 text-sm">Incluye:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-600 ml-2">
                            {beneficios.map((beneficio, index) => (
                                <li key={index} className="text-sm leading-relaxed">
                                    {beneficio.trim()}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const obtenerDatos = async () => {
            try {
                // Obtener datos de matrícula y plan
                const hijoSeleccionado = JSON.parse(localStorage.getItem('hijoSeleccionado'));
                if (hijoSeleccionado && hijoSeleccionado.id_child) {
                    // Obtener matrícula y verificar estado de pago
                    const response = await obtenerMatriculaPorId(hijoSeleccionado.id_child);
                    const idMatricula = response.matricula.matricula.id_matricula;
                    const matriculaData = response.matricula.matricula;
                    setMatriculaId(idMatricula);
                    
                    // Verificar si tiene pagos anteriores
                    const tienePagos = await verificarPagosAnteriores(idMatricula);
                    setTienePagosAnteriores(tienePagos);
                    
                    if (tienePagos) {
                        // Si tiene pagos anteriores, cargar planes
                        await cargarPlanes();
                        
                        // Obtener plan actual
                        if (matriculaData.id_plan) {
                            const planesResponse = await obtenerPlanes();
                            if (planesResponse.success && planesResponse.data) {
                                const planEncontrado = planesResponse.data.find(p => p.id_plan === matriculaData.id_plan);
                                if (planEncontrado) {
                                    setPlanActual(planEncontrado);
                                    // planSeleccionado ahora viene como prop
                                    setAmount(planEncontrado.monto);
                                }
                            }
                        }

                    } else {
                        // Si no tiene pagos anteriores, usar el plan seleccionado que viene como prop
                        if (planSeleccionado && planSeleccionado.monto) {
                            const precio = Number(planSeleccionado.monto);
                            setAmount(isNaN(precio) ? 0 : precio);
                        } else {
                            // Fallback: obtener precio del plan desde la información del hijo
                            const user = JSON.parse(localStorage.getItem('user'));
                            const userId = user?.id_usuario || user?.id;
                            
                            if (user && userId) {
                                try {
                                    const hijosResponse = await verificarHijos(userId);
                                    const hijosActivos = hijosResponse.hijos?.filter(hijo => hijo.estado_matricula === 'Activa') || [];
                                    const hijo = hijosActivos.find(h => h.id_child === hijoSeleccionado.id_child);
                                    
                                    if (hijo && hijo.plan_monto) {
                                        const precio = Number(hijo.plan_monto);
                                        setAmount(isNaN(precio) ? 0 : precio);
                                    } else {
                                        console.warn('No se pudo obtener el precio del plan para el hijo:', hijoSeleccionado.id_child);
                                        setAmount(0);
                                    }
                                } catch (err) {
                                    console.error('Error al obtener precio del plan:', err);
                                    setAmount(0);
                                }
                            }
                        }
                    }
                    await verificarEstadoPago(idMatricula);
                } else {
                    console.warn('No se pudo obtener el precio de matrícula, usando valor por defecto 0.00');
                    setAmount(0);
                }

                const cuentasResponse = await obtenerCuentasBancarias();
                if (cuentasResponse.success) {
                    setCuentasBancarias(cuentasResponse.data);
                }
            } catch (err) {
                setError('Error al obtener información necesaria');
            } finally {
                setIsLoading(false);
            }
        };
        obtenerDatos();
    }, []);

    const validarArchivo = (file) => {
        setArchivoError('');
        
        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
            setArchivoError('Solo se permiten archivos de imagen');
            showWarningToast('Solo se permiten archivos de imagen');
            return false;
        }

        // Validar tamaño (5MB = 5 * 1024 * 1024 bytes)
        const tamañoMaximo = 5 * 1024 * 1024;
        if (file.size > tamañoMaximo) {
            setArchivoError('El archivo no debe superar los 5MB');
            showWarningToast('El archivo no debe superar los 5MB');
            return false;
        }

        return true;
    };

    const handleComprobanteChange = (e) => {
        const archivo = e.target.files[0];
        if (archivo && validarArchivo(archivo)) {
            setComprobante(archivo);
            setNombreArchivo(archivo.name);
        } else {
            e.target.value = ''; // Limpiar el input
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file && validarArchivo(file)) {
            setComprobante(file);
            setNombreArchivo(file.name);
        }
    };

    const eliminarComprobante = () => {
        setComprobante(null);
        setNombreArchivo('');
    };

    const handleSubmit = async () => {
        const amountValue = amount || 0;
        if (!comprobante || !matriculaId || !amountValue) {
            setError('Complete todos los campos requeridos');
            return;
        }
        setIsProcessing(true);
        setError('');

        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                // Actualizar plan si es necesario antes del pago
                if (planSeleccionado && planActual && planSeleccionado.id_plan !== planActual.id_plan) {
                    console.log('Actualizando plan de matrícula:', matriculaId, 'al plan:', planSeleccionado.id_plan);
                    const updateResponse = await actualizarMatricula(matriculaId, {
                        id_plan: planSeleccionado.id_plan
                    });
                    
                    if (!updateResponse.success) {
                        console.error('Error al actualizar plan:', updateResponse.message);
                        showErrorToast('Error al actualizar el plan');
                        setIsProcessing(false);
                        return;
                    }
                }
                
                // Generar código de transferencia único (solo números)
                const codigoTransferencia = Date.now().toString();
                
                // Debug: Mostrar datos antes de enviar la petición
                console.log('=== DATOS ENVIADOS AL BACKEND (transferencia.js) ===');
                console.log('amountValue:', amountValue);
                console.log('matriculaId:', matriculaId);
                console.log('mes:', new Date().getMonth() + 1);
                console.log('año:', new Date().getFullYear());
                console.log('codigoTransferencia:', codigoTransferencia);
                console.log('reader.result (primeros 100 chars):', reader.result ? reader.result.substring(0, 100) + '...' : 'null');
                console.log('===================================================');
                
                const response = await pagoTransferencia(
                    amountValue,
                    matriculaId,
                    new Date().getMonth() + 1,
                    new Date().getFullYear(),
                    reader.result,
                    codigoTransferencia,
                    planSeleccionado?.id_plan // Enviar el id del plan seleccionado
                );
                if (response.success) {
                    showSuccessToast(planActual && planSeleccionado && planSeleccionado.id_plan !== planActual.id_plan ?
                        'Plan actualizado y comprobante enviado exitosamente' :
                        'Comprobante enviado exitosamente'
                    );
                    
                    // Limpiar localStorage
                    localStorage.removeItem('planSeleccionadoTransferencia');
                    
                    setComprobante(null);
                    
                    // Recargar página después de un breve delay
                    setTimeout(() => {
                        window.location.reload();
                    }, 2000);
                } else {
                    setError('Error al procesar el pago. Intente nuevamente.');
                }
            } catch (err) {
                let mensajeError = 'Error al procesar el pago';
                
                if (err.response?.status === 404) {
                    mensajeError = 'Servicio no disponible. Verifique que el servidor esté funcionando.';
                } else if (err.response?.data?.message) {
                    mensajeError = err.response.data.message;
                } else if (err.message) {
                    mensajeError = err.message;
                }
                
                setError(mensajeError);
                showErrorToast(mensajeError);
            } finally {
                setIsProcessing(false);
            }
        };
        reader.readAsDataURL(comprobante);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <div className="w-8 h-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                <p className="text-gray-600 mt-4">Verificando estado de pagos...</p>
            </div>
        );
    }



    if (!mostrarPago) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center p-12 backdrop-blur-sm bg-white/40 rounded-3xl shadow-sm border border-green-100/50 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col items-center space-y-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-light text-gray-800">¡Estás al día con tus pagos!</h2>
                        <p className="text-gray-500 font-light">No hay pagos pendientes en este momento</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 px-2 sm:px-4">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            <div className="max-w-4xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow-sm p-4 sm:p-8">
                <h2 className="text-2xl sm:text-3xl font-light text-gray-800 text-center mb-8 sm:mb-12">Transferencia Bancaria</h2>
                
                {/* Mostrar información del plan */}

                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12">
                    <div className="space-y-6 sm:space-y-8">
                        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg sm:rounded-xl">
                            <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4 sm:mb-6">Seleccione un Banco</h3>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                {cuentasBancarias.map((cuenta) => (
                                    <div
                                        key={cuenta.id_cuenta}
                                        onClick={() => setCuentaSeleccionada(cuenta)}
                                        className={`p-3 sm:p-4 rounded-lg cursor-pointer transition-all ${
                                            cuentaSeleccionada?.id_cuenta === cuenta.id_cuenta
                                            ? 'bg-blue-100 border-2 border-blue-400'
                                            : 'bg-white border-2 border-gray-100 hover:border-blue-200'
                                        }`}
                                    >
                                        <h4 className="text-xs sm:text-sm font-medium text-gray-800">{cuenta.banco}</h4>
                                    </div>
                                ))}
                            </div>

                            {cuentaSeleccionada && (
                                <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4 bg-white p-3 sm:p-4 rounded-lg border border-gray-100">
                                    <div className="flex justify-between items-center text-xs sm:text-sm">
                                        <span className="text-gray-600">tipo de cuenta</span>
                                        <span className="font-medium text-gray-800">{cuentaSeleccionada.tipo_cuenta}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs sm:text-sm">
                                        <span className="text-gray-600">número de cuenta</span>
                                        <span className="font-medium text-gray-800">{cuentaSeleccionada.numero_cuenta}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs sm:text-sm">
                                        <span className="text-gray-600">beneficiario</span>
                                        <span className="font-medium text-gray-800">{cuentaSeleccionada.beneficiario}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs sm:text-sm">
                                        <span className="text-gray-600">Cédula</span>
                                        <span className="font-medium text-gray-800">{cuentaSeleccionada.ruc}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-blue-50 p-4 sm:p-6 rounded-lg sm:rounded-xl">
                            <span className="text-xs sm:text-sm text-blue-600">Monto a transferir</span>
                            <div className="text-2xl sm:text-4xl font-light text-blue-700 mt-2">${Number(planSeleccionado?.monto || amount || 0).toFixed(2)}</div>
                        </div>
                    </div>

                    <div className="space-y-4 sm:space-y-6">


                        <div>
                            <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-4 sm:mb-6">Comprobante de Pago</h3>
                            
                            <div className="space-y-4">
                                {comprobante ? (
                                    <div className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                                        <div className="flex items-center space-x-3">
                                            <FaFile className="text-blue-500" size={20} />
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">{nombreArchivo}</p>
                                                <button 
                                                    onClick={() => setImagenAmpliada(true)}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    Ver imagen
                                                </button>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={eliminarComprobante}
                                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <FaTrash size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <div 
                                            className={`relative ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
                                            onDragEnter={handleDragEnter}
                                            onDragLeave={handleDragLeave}
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                        >
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleComprobanteChange}
                                                className="hidden"
                                                id="comprobante"
                                            />
                                            <label
                                                htmlFor="comprobante"
                                                className="flex flex-col items-center justify-center space-y-2 w-full px-3 sm:px-4 py-4 sm:py-6 border-2 border-dashed rounded-lg sm:rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                                            >
                                                <FaExchangeAlt className="text-gray-500" size={20} />
                                                <div className="text-center">
                                                    <p className="text-xs sm:text-sm text-gray-600">Arrastra y suelta la imagen aquí o</p>
                                                    <p className="text-xs sm:text-sm text-blue-600 font-medium">Haz clic para seleccionar</p>
                                                    <p className="text-xs text-gray-500 mt-1">Solo imágenes (máx. 5MB)</p>
                                                </div>
                                            </label>
                                        </div>
                                        {archivoError && (
                                            <p className="text-red-500 text-xs sm:text-sm mt-2">{archivoError}</p>
                                        )}
                                    </div>
                                )}

                                {imagenAmpliada && comprobante && (
                                    <div 
                                        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                                        onClick={() => setImagenAmpliada(false)}
                                    >
                                        <img 
                                            src={URL.createObjectURL(comprobante)}
                                            alt="Comprobante ampliado"
                                            className="max-w-full max-h-[90vh] object-contain rounded-lg"
                                        />
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="mt-4 text-rose-500 text-xs sm:text-sm bg-rose-50 p-3 rounded-lg">{error}</div>
                            )}

                            <button
                                onClick={handleSubmit}
                                disabled={!comprobante || isProcessing || archivoError}
                                className={`
                                    w-full py-3 sm:py-4 mt-4 sm:mt-6 rounded-lg sm:rounded-xl text-white text-sm font-medium transition-all
                                    ${comprobante && !isProcessing && !archivoError
                                        ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-xl' 
                                        : 'bg-gray-200 cursor-not-allowed'
                                    }
                                `}
                            >
                                {isProcessing ? 'Procesando...' : 'Confirmar pago'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Transferencia;
